import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { subscribeRealtime } from "../lib/realtime";
import Avatar from "../components/Avatar";
import VerifiedBadge from "../components/VerifiedBadge";
import { Send, MessageSquare } from "lucide-react";

function timeShort(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

export default function MessagesPage() {
  const { conversationId } = useParams();
  const nav = useNavigate();
  const [convs, setConvs] = useState(null);
  const [requests, setRequests] = useState([]);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const loadConvs = useCallback(async () => {
    const { data } = await api.get("/conversations");
    setConvs(data);
    try {
      const { data: reqs } = await api.get("/message-requests");
      setRequests(reqs);
    } catch (err) {
      console.warn("Could not load message requests:", err?.message);
    }
  }, []);

  const actOnRequest = async (req, action) => {
    const { data } = await api.post(`/message-requests/${req.id}/${action}`);
    setRequests((rs) => rs.filter((r) => r.id !== req.id));
    if (action === "accept") {
      await loadConvs();
      nav(`/messages/${data.id}`);
    }
  };

  const loadMessages = useCallback(async () => {
    if (!conversationId) return;
    try {
      const { data } = await api.get(`/conversations/${conversationId}/messages`);
      setMessages(data);
    } catch (_) {
      nav("/messages");
    }
  }, [conversationId, nav]);

  useEffect(() => {
    loadConvs();
  }, [loadConvs]);

  useEffect(() => {
    loadMessages();
    if (!conversationId) return;
    const t = setInterval(loadMessages, 15000); // fallback; realtime below is primary
    return () => clearInterval(t);
  }, [conversationId, loadMessages]);

  useEffect(() => {
    const unsub = subscribeRealtime((event) => {
      if (event.type !== "message") return;
      if (event.conversation_id === conversationId) loadMessages();
      loadConvs();
    });
    return unsub;
  }, [conversationId, loadMessages, loadConvs]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const active = convs?.find((c) => c.id === conversationId);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      const { data } = await api.post(`/conversations/${conversationId}/messages`, {
        text: text.trim(),
      });
      setMessages((m) => [...m, data]);
      setText("");
      loadConvs();
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-6" data-testid="messages-page">
      <h1 className="font-display text-4xl uppercase tracking-tight text-white mb-4">Messages</h1>
      <div className="grid grid-cols-1 md:grid-cols-[300px_1fr] bg-[#121212] border border-[#262626] rounded-sm overflow-hidden min-h-[560px]">
        {/* Conversation list */}
        <div
          className={`border-r border-[#262626] ${conversationId ? "hidden md:block" : ""}`}
          data-testid="conversation-list"
        >
          {requests.length > 0 && (
            <div className="border-b border-[#262626] p-3" data-testid="message-requests-section">
              <div className="text-[10px] uppercase tracking-widest text-[#737373] mb-2">
                Message requests — you decide
              </div>
              <ul className="space-y-2">
                {requests.map((r) => (
                  <li key={r.id} data-testid={`message-request-${r.id}`} className="bg-[#0a0a0a] border border-[#404040] p-3 rounded-sm">
                    <div className="flex items-center gap-2">
                      <Avatar url={r.from?.avatar_url} name={r.from?.name} size={30} />
                      <div className="min-w-0 flex-1">
                        <div className="text-xs text-white font-semibold truncate">{r.from?.name}</div>
                        <div className="text-[10px] uppercase tracking-widest text-[#737373]">{r.from?.role}</div>
                      </div>
                    </div>
                    {r.text && <p className="text-xs text-[#a3a3a3] mt-2">“{r.text}”</p>}
                    <div className="flex gap-2 mt-2">
                      <button
                        data-testid={`request-accept-${r.id}`}
                        onClick={() => actOnRequest(r, "accept")}
                        className="flex-1 py-1.5 text-xs bg-white text-black font-semibold rounded-sm hover:bg-gray-200"
                      >
                        Accept
                      </button>
                      <button
                        data-testid={`request-decline-${r.id}`}
                        onClick={() => actOnRequest(r, "decline")}
                        className="flex-1 py-1.5 text-xs border border-[#404040] text-white rounded-sm hover:bg-[#1a1a1a]"
                      >
                        Decline
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {convs === null ? (
            <div className="p-4 text-xs text-[#737373]">Loading…</div>
          ) : convs.length === 0 ? (
            <div className="p-6 text-sm text-[#a3a3a3]" data-testid="conversations-empty">
              No conversations yet. Visit a profile and hit{" "}
              <span className="text-white font-semibold">Message</span>.
            </div>
          ) : (
            <ul className="divide-y divide-[#262626]">
              {convs.map((c) => (
                <li key={c.id}>
                  <Link
                    to={`/messages/${c.id}`}
                    data-testid={`conversation-${c.id}`}
                    className={`flex items-center gap-3 p-4 transition-colors ${
                      c.id === conversationId ? "bg-[#1a1a1a]" : "hover:bg-[#1a1a1a]"
                    }`}
                  >
                    <Avatar url={c.other?.avatar_url} name={c.other?.name} size={42} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white font-semibold truncate flex items-center gap-1.5">
                        {c.other?.name}
                        <VerifiedBadge role={c.other?.role} verified={c.other?.verified} size={12} />
                      </div>
                      <div className="text-xs text-[#a3a3a3] truncate">
                        {c.last_message || "Say hello"}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <span className="text-[10px] text-[#737373]">{timeShort(c.last_at)}</span>
                      {c.unread > 0 && (
                        <span
                          data-testid={`conversation-unread-${c.id}`}
                          className="min-w-[18px] h-[18px] px-1 bg-white text-black text-[10px] font-bold rounded-full flex items-center justify-center"
                        >
                          {c.unread}
                        </span>
                      )}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Chat pane */}
        {conversationId ? (
          <div className="flex flex-col h-[560px]" data-testid="chat-pane">
            <div className="flex items-center gap-3 px-4 py-3 border-b border-[#262626]">
              <Link to="/messages" className="md:hidden text-[#a3a3a3] text-sm hover:text-white">
                ←
              </Link>
              {active?.other && (
                <>
                  <Link to={`/profile/${active.other.id}`}>
                    <Avatar url={active.other.avatar_url} name={active.other.name} size={36} />
                  </Link>
                  <div>
                    <Link
                      to={`/profile/${active.other.id}`}
                      className="text-sm text-white font-semibold hover:underline inline-flex items-center gap-1.5"
                    >
                      {active.other.name}
                      <VerifiedBadge role={active.other.role} verified={active.other.verified} size={12} />
                    </Link>
                    <div className="text-[10px] uppercase tracking-widest text-[#737373]">
                      {active.other.role && active.other.role !== "player"
                        ? active.other.role
                        : active.other.position || "Player"}
                      {active.other.team ? ` · ${active.other.team}` : ""}
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="messages-thread">
              {messages.length === 0 && (
                <div className="text-xs text-[#737373] text-center pt-10">
                  Start the conversation.
                </div>
              )}
              {messages.map((m) => (
                <div
                  key={m.id}
                  data-testid={`message-${m.id}`}
                  className={`flex ${m.is_me ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[75%] px-4 py-2.5 rounded-sm text-sm whitespace-pre-wrap ${
                      m.is_me
                        ? "bg-white text-black"
                        : "bg-[#0a0a0a] border border-[#262626] text-white"
                    }`}
                  >
                    {m.text}
                    <div
                      className={`text-[9px] uppercase tracking-widest mt-1 ${
                        m.is_me ? "text-black/50" : "text-[#737373]"
                      }`}
                    >
                      {timeShort(m.created_at)}
                    </div>
                  </div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>

            <form onSubmit={send} className="flex gap-2 p-3 border-t border-[#262626]">
              <input
                data-testid="message-input"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Write a message…"
                className="flex-1 bg-[#0a0a0a] border border-[#262626] focus:border-white outline-none px-4 py-2.5 text-sm text-white rounded-sm transition-colors"
              />
              <button
                data-testid="message-send"
                disabled={sending || !text.trim()}
                className="px-4 bg-white text-black rounded-sm hover:bg-gray-200 disabled:opacity-50 transition-colors"
                aria-label="Send"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
          </div>
        ) : (
          <div className="hidden md:flex flex-col items-center justify-center text-center p-8">
            <MessageSquare className="w-10 h-10 text-[#404040] mb-3" />
            <div className="text-[#a3a3a3] text-sm max-w-xs">
              Select a conversation, or hit Message on any player or scout profile.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
